import {Checkbox, Modal} from '@mantine/core';
import type React from 'react';
import {MdChevronRight, MdClose, MdEvent} from 'react-icons/md';
import {GSSKT_HOST} from '../../../../../config/constants/hosts';
import {PROFILE_BASE} from '../../../../../config/constants/routes';
import {CommonLink} from '../../../../../shared/components/CommonLink';
import {buildScheduleGtmContent} from '../../../../../shared/services/buildScheduleGtmContent';
import {ScheduleButtonNewDesign} from '../../../../recruitment/components/ScheduleButton';

type Props = {
  opened: boolean;
  onClose: () => void;
  checked: boolean;
  setChecked: (value: boolean) => void;
  companyId: number;
  companyName: string;
  recruitmentLinkHref: string;
  recruitments: CompletionRecruitment[];
  pagePath: string;
};

export type CompletionRecruitment = {
  id: number;
  title: string;
  detailPageURL: string;
  deadlineText: string;
  deadlineColor: string;
  schedule: {
    id: number;
    isCalendarRegistered: boolean;
    calendarRegisteredCount: number;
  };
  company: {
    id: number;
    name: string;
  };
};

export const CompanyFollowCompletionModal: React.FC<Props> = ({
  opened,
  onClose,
  checked,
  setChecked,
  companyId,
  companyName,
  recruitmentLinkHref,
  recruitments,
  pagePath,
}) => {
  const hasRecruitments = recruitments.length > 0;

  const baseGtmContent = {
    company_id: companyId,
    company_name: companyName,
    page_path: pagePath,
  };

  const scoutAppealText = (
    <>
      フォローをするとフォローした企業からスカウトが届く確率も高まります。
      <CommonLink href={PROFILE_BASE} className="text-link pc:hover:opacity-80">
        プロフィール
      </CommonLink>
      を充実させることで、あなたの魅力を企業にアピールしましょう！
    </>
  );

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      centered
      size="sm"
      withCloseButton={false}
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      <div className="flex flex-col gap-16 pb-8">
        <button
          type="button"
          aria-label="閉じる"
          onClick={onClose}
          className="self-start text-black-2 hover:opacity-70"
        >
          <MdClose size={20} />
        </button>
        <div className="flex justify-center">
          <MdEvent size={42} className="text-primary-1" />
        </div>
        <p className="text-xl font-bold text-black-1 text-center">
          フォローが完了しました！
        </p>
        <p className="text-base font-bold text-black-1 text-center">
          {companyName}
        </p>
        {!hasRecruitments && (
          <p className="text-sm text-black-1">{scoutAppealText}</p>
        )}
        {hasRecruitments && (
          <>
            <p className="text-sm font-bold text-black-1 text-center">
              募集の予定を追加しましょう
            </p>
            <p className="text-xs text-black-2">
              予定を追加するとリマインドなどの情報が通知されます
            </p>
            <ul className="flex flex-col gap-16">
              {recruitments.map((recruitment) => (
                <li
                  key={recruitment.id}
                  className="flex items-center justify-between gap-8"
                >
                  <div className="flex flex-col gap-4 min-w-0">
                    <p className="text-sm font-bold text-black-1 line-clamp-2">
                      {recruitment.title}
                    </p>
                    {recruitment.deadlineText && (
                      <p
                        className={`text-xs text-black-2 ${recruitment.deadlineColor}`}
                      >
                        締切: {recruitment.deadlineText}
                      </p>
                    )}
                  </div>
                  <div className="shrink-0">
                    <ScheduleButtonNewDesign
                      scheduleId={recruitment.schedule.id}
                      isRegisteredInitial={
                        recruitment.schedule.isCalendarRegistered
                      }
                      registeredCountInitial={
                        recruitment.schedule.calendarRegisteredCount
                      }
                      buttonStyle="textWithBorder"
                      recruitmentData={{
                        companyId: recruitment.company.id,
                        companyName: recruitment.company.name,
                        entryTitle: recruitment.title,
                        detailURL: `${GSSKT_HOST}${recruitment.detailPageURL}`,
                      }}
                      dataGtmClick="recruitment-schedule-add"
                      dataGtmContent={buildScheduleGtmContent({
                        scheduleId: recruitment.schedule.id,
                        location: 'company-follow-completion-modal',
                      })}
                    />
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}
        {hasRecruitments && (
          <>
            <CommonLink
              href={recruitmentLinkHref}
              onClick={onClose}
              className="flex items-center text-sm font-bold text-primary hover:opacity-70"
              data-gtm-click="follow-completion-view-recruitment-list"
              data-gtm-content={JSON.stringify(baseGtmContent)}
            >
              この企業の募集情報をみる
              <MdChevronRight size={18} />
            </CommonLink>
            <hr className="border-line" />
            <p className="text-xs text-black-2">{scoutAppealText}</p>
          </>
        )}
        <hr className="border-line" />
        <Checkbox
          label="しばらくこのメッセージを表示しない"
          checked={checked}
          onChange={(e) => setChecked(e.currentTarget.checked)}
          size="sm"
          data-gtm-click="follow-completion-suppress"
          data-gtm-content={JSON.stringify(baseGtmContent)}
        />
      </div>
    </Modal>
  );
};
